// src/UserResults.tsx
//
// Výsledky závodů pro střelce (krok 7 redesignu): filtry rok / disciplína /
// kategorie, akordeon závodů s tabulkou výsledků a roční hodnocení
// „nejlepších N závodů" zvlášť pro každou disciplínu.
//
// Data:
//  - seznam závodů z useRaces: seznam z paměti relace se ukáže hned a při
//    každém mountu stránky se načte znovu (stale-while-revalidate; s
//    UserResultsPage sdílí jeden dotaz = jedno čtení kolekce na stránku),
//    takže se projeví nová publikace výsledků i změna stavu live závodu,
//  - výsledky závodu přes loadRaceResults (localStorage cache lks_results_{id}
//    invalidovaná podle resultsPublishedAt – modul ji čte i zapisuje jako dřív);
//    stav výsledků si pamatuje publikaci (resultsPublishedAt), ke které patří,
//    a když obnovený seznam přinese novější, čte se znovu – dosavadní výsledky
//    zůstávají vidět, než dorazí nové,
//  - roční hodnocení computeSeasonRanking (původní algoritmus přesunutý 1:1),
//  - kategorie přes categories.ts (resolveEntryCategoryId): uložené categoryId
//    (publikace z live scoringu od kroku 9, vč. nováčků) má přednost, jinak se
//    legacy hodnoty ("člen-žena", "Muž", "Ženy"…) mapují při čtení, takže filtr
//    najde i starší live závody (backlog #8),
//  - jméno střelce přes resultEntryName (bezpečnost a): e-mail ani uid, které
//    jako jméno uložila dřívější verze živého bodování, se neukážou („St. č. N",
//    jinak „#číslo"); sdílení vlastního výsledku pak vezme jméno z profilu.
//
// Stav výsledků drží obrazovka per závod ({loading | ready | error} +
// publishedAt); guard proti dvojímu načtení čte z ref, ne ze stavu
// zachyceného v closure (backlog #28). Chyba čtení = ErrorState s opakováním,
// nikdy tiché „bez výsledků". Procenta mají jednu definici – podíl na
// výsledku vítěze celého závodu (computeRacePercent), stejně jako v ročním
// hodnocení.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DISCIPLINE_LIST,
  disciplineOf,
  toDisciplineId,
  type Discipline,
  type DisciplineId,
} from "./utils/disciplines";
import { DisciplineBadge } from "./ui/DisciplineBadge";
import {
  DataTable,
  type DataTableColumn,
  type DataTableRowProps,
} from "./ui/DataTable";
import { Section, SectionHeader } from "./ui/Section";
import { Field } from "./ui/Field";
import { ChoiceGroup, Toggle, type ChoiceOption } from "./ui/Toggle";
import { EmptyState, ErrorState, LoadingState } from "./ui/AsyncState";
import { scrollIntoMain } from "./ui/AppShell";
import { fmtFixed, fmtPoints, pointsDecimals } from "./utils/formatPoints";
import { useIsAdmin } from "./utils/useAuth";
import { useCurrentUser } from "./utils/useCurrentUser";
import { reportError, reportWarning } from "./utils/reportError";
import { firebaseErrorMessage } from "./utils/firebaseErrors";
import {
  RESULT_CATEGORIES,
  categoryLabel,
  categoryOrder,
  displayEntryCategory,
  isResultCategoryId,
  resolveEntryCategoryId,
  type ResultCategoryId,
} from "./utils/categories";
import {
  bestRacesLabel,
  bestRacesOptions,
  computeRacePercent,
  computeSeasonRanking,
  isSameShooter,
  raceTopScore,
  rankCategoryEntries,
  rankRaceEntries,
  seasonRaces,
  type BestRacesCount,
  type RaceResult,
  type RaceStageResult,
  type SeasonRace,
  type SeasonRankingRow,
  type SeasonValueMode,
} from "./utils/seasonRanking";
import {
  countRaceResults,
  getLoadedRaces,
  loadRaceResults,
  useRaces,
  type Race,
} from "./utils/useRaces";
import { shootOffIdOf, shootOffKeys, tiebreakerOf } from "./utils/shootOff";
import {
  computeMySeason,
  heldSeasonRaces,
  isMyEntry,
  type Me,
  type MySeason,
} from "./utils/mySeason";
import { MySeasonSection } from "./MySeasonSection";
import { ShareResultButton, type ShareResultData } from "./ShareResult";
import { canOpenExternalLinks, isNativeApp } from "./utils/platform";
import { profilePublicName, resultEntryName, safeShooterName } from "./utils/shooterName";

// ─────────────────────────────────────────────────────────────
//  Typy a konstanty
// ─────────────────────────────────────────────────────────────

/**
 * Výsledky jednoho závodu, jak je drží obrazovka (chybějící klíč = ještě
 * nenačítáno). `publishedAt` = races.resultsPublishedAt (millis), ke kterému
 * stav patří – obnovený seznam závodů s jinou hodnotou znamená novou
 * publikaci a nové čtení; `stale` jsou do jeho dokončení zobrazené výsledky
 * předchozí publikace (null = první načtení).
 */
type RaceResultsState =
  | { status: "loading"; publishedAt: number; stale: RaceResult[] | null }
  | { status: "ready"; publishedAt: number; results: RaceResult[] }
  | { status: "error"; publishedAt: number; error: unknown };

type ResultsByRace = { [raceId: string]: RaceResultsState | undefined };

/** Načtené výsledky pro computeSeasonRanking – jen závody ve stavu ready. */
type EntriesByRace = { [raceId: string]: RaceResult[] };

/** Členské číslo přihlášeného střelce (users.sequenceNumber) pro isSameShooter. */
type ShooterId = string | number | null | undefined;

/** Nastavení ročního hodnocení – každá disciplína má vlastní (plán kroku 7). */
interface SeasonSettings {
  bestN: BestRacesCount;
  mode: SeasonValueMode;
}

type SeasonSettingsByDiscipline = Record<DisciplineId, SeasonSettings>;

/**
 * Požadavek stránky na otevření závodu v seznamu (karta „Výsledky" ukončeného
 * live závodu); `token` odliší opakované kliknutí na stejný závod.
 */
export interface OpenRaceRequest {
  raceId: string;
  token: number;
}

interface UserResultsProps {
  openRequest?: OpenRaceRequest | null;
}

/** Řádek tabulky závodu: výsledek + pořadí ve vyfiltrovaném seznamu (nezávislé na řazení sloupců). */
interface RaceTableRow extends RaceResult {
  rank: number;
}

/** Řádek ročního hodnocení + pořadí podle průměru. */
interface SeasonTableRow extends SeasonRankingRow {
  rank: number;
}

/** Naposledy použitá disciplína ve filtru (výchozí hodnota při příští návštěvě). */
const DISCIPLINE_STORAGE_KEY = "lks_results_discipline";

/** Pevná šířka sloupce Kategorie – stejná v tabulce závodu i v ročním hodnocení. */
const CATEGORY_COL_WIDTH = "8.5rem";

const DEFAULT_SEASON_SETTINGS: SeasonSettings = { bestN: "all", mode: "percent" };

const MODE_OPTIONS: ChoiceOption<SeasonValueMode>[] = [
  { value: "percent", label: "Procenta" },
  { value: "points", label: "Body" },
];

// ─────────────────────────────────────────────────────────────
//  Pomocné funkce
// ─────────────────────────────────────────────────────────────

const initialSeasonSettings = (): SeasonSettingsByDiscipline =>
  DISCIPLINE_LIST.reduce(
    (acc, d) => ({ ...acc, [d.id]: { ...DEFAULT_SEASON_SETTINGS } }),
    {} as SeasonSettingsByDiscipline
  );

/** Disciplína uložená z minulé návštěvy; bez záznamu (nebo bez localStorage) „všechny". */
function readStoredDiscipline(): DisciplineId | "all" {
  try {
    const stored = localStorage.getItem(DISCIPLINE_STORAGE_KEY);
    if (stored === "all") return "all";
    if (stored && DISCIPLINE_LIST.some((d) => d.id === stored)) {
      return stored as DisciplineId;
    }
  } catch {
    // localStorage nedostupné – výchozí hodnota
  }
  return "all";
}

/** Uloží disciplínu jako výchozí pro příští návštěvu – jen při volbě v selectu, ne při programovém přepnutí. */
function storeDiscipline(value: DisciplineId | "all"): void {
  try {
    localStorage.setItem(DISCIPLINE_STORAGE_KEY, value);
  } catch {
    // localStorage nedostupné – nic
  }
}

/**
 * Výchozí rok filtru: aktuální rok, pokud má v seznamu závodů načteném dřív
 * v relaci nějaký závod, jinak „Všechny". Odvozuje se už při inicializaci
 * (getLoadedRaces), ať po návratu na stránku neblikne prázdný stav; před
 * prvním načtením seznamu se bere aktuální rok a totéž pak dořeší layout
 * efekt v obrazovce.
 */
type LoadedRace = NonNullable<ReturnType<typeof getLoadedRaces>>[number];

/**
 * Jedno pravidlo pro výchozí rok (start, obnova seznamu i Zrušit filtry):
 * nejnovější rok, který má závod s výsledky (enableResults); bez něj „Všechny".
 * „Všechny roky" načítá výsledky všech let najednou, proto až jako poslední.
 */
function defaultFilterYear(races: LoadedRace[]): number | "all" {
  let newest: number | null = null;
  races.forEach((r) => {
    if (r.enableResults && (newest === null || r.year > newest)) newest = r.year;
  });
  return newest === null ? "all" : newest;
}

function initialFilterYear(): number | "all" {
  const loaded = getLoadedRaces();
  if (!loaded) return new Date().getFullYear();
  return defaultFilterYear(loaded);
}

/** Výsledky k zobrazení: načtené, nebo (během nového čtení) předchozí publikace; null = nic. */
const resultsOf = (state: RaceResultsState | undefined): RaceResult[] | null => {
  if (!state) return null;
  if (state.status === "ready") return state.results;
  if (state.status === "loading") return state.stale;
  return null;
};

/** Zkrácený název závodu do hlavičky sloupce; celý název nese title hlavičky. */
const shortRaceName = (name: string, max = 12): string => {
  const full = (name || "").trim();
  if (full.length <= max) return full;
  const cut = full.slice(0, max);
  const space = cut.lastIndexOf(" ");
  return `${(space >= 5 ? cut.slice(0, space) : cut).trim()}…`;
};

/** Datum závodu bez roku ("12. 4.") – rok je v titulku ročního hodnocení. */
const fmtShortDate = (date: string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return `${d.getDate()}. ${d.getMonth() + 1}.`;
};

/** Datum závodu česky ("12. 4. 2026"); nečitelné datum se vypíše, jak je uložené. */
const fmtRaceDate = (date: string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("cs-CZ");
};

/** "1 střelec" / "3 střelci" / "12 střelců" */
const shooterCountLabel = (n: number): string =>
  `${n} ${n === 1 ? "střelec" : n < 5 ? "střelci" : "střelců"}`;

/** Výsledek má detail stanovišť (results_stages existuje a není prázdné). */
const hasStages = (
  result: RaceResult
): result is RaceResult & { stages: RaceStageResult[] } =>
  Array.isArray(result.stages) && result.stages.length > 0;

/**
 * Záznam publikace z živého bodování dřívější verzí aplikace (bez pole
 * categoryId): nováčci nejsou označení a nečlenové nejsou zařazení do
 * kategorií. Zmizí po nové publikaci závodu (Živé výsledky → Publikovat).
 */
const isLegacyLiveResult = (result: RaceResult): boolean =>
  result.entry.source === "live_scoring" && result.entry.categoryId === undefined;

/**
 * Štítek „R" u jména: pořadí při shodě bodů určil rozstřel (utils/shootOff).
 * Text pro čtečky obrazovky je skrytý vizuálně, title je nápověda myší.
 */
const ShootOffBadge: React.FC = () => (
  <span
    className="badge text-bg-warning ms-1 align-text-bottom"
    title="Pořadí při shodě bodů určil rozstřel"
  >
    R<span className="visually-hidden"> – pořadí určil rozstřel</span>
  </span>
);

const LEGACY_LIVE_NOTE =
  "Výsledky tohoto závodu ze živého bodování byly zveřejněny dřívější verzí aplikace: nováčci v nich nejsou označení a nečlenové nejsou zařazení do kategorií. Filtr kategorie proto nemusí ukázat všechny střelce.";

/**
 * Zvýraznění vlastního řádku. Bootstrap 5.3 bere pozadí buněk z --bs-table-bg,
 * inline background na <tr> by neprosvítilo (platí i pro přilepený první
 * sloupec); backgroundColor je pojistka pro mobilní režim karet DataTable
 * (obě tabulky obrazovky dnes používají mobile="scroll").
 */
const ownRowProps = (isMine: boolean): DataTableRowProps =>
  isMine
    ? {
        style: {
          "--bs-table-bg": "var(--lks-brand-100, #e0f2f1)",
          backgroundColor: "var(--lks-brand-100, #e0f2f1)",
          borderColor: "var(--lks-brand-200, #b2dfdb)",
          fontWeight: 600,
        } as React.CSSProperties,
        title: "Váš výsledek",
      }
    : {};

// ─────────────────────────────────────────────────────────────
//  Detail stanovišť
// ─────────────────────────────────────────────────────────────

/**
 * Detail stanovišť pod řádkem výsledku: stanoviště vedle sebe, body pod nimi
 * (svislý seznam byl na široké obrazovce horší). Všechny sloupce stejně
 * široké, nejméně 3rem – názvy se lámou jen na mezerách (ne po písmenech)
 * a při mnoha stanovištích tabulka raději scrolluje v .table-responsive.
 * Ten má šířku 0 a min-width 100 %: v rozbaleném řádku tabulky výsledků tak
 * vyplní celou šířku, ale do šířky sloupců nepřispívá – jinak by na mobilu
 * detail s mnoha stanovišti roztáhl celou tabulku do strany. `id` je cíl
 * aria-controls tlačítka se šipkou.
 */
const StageDetail: React.FC<{ id: string; stages: RaceStageResult[] }> = ({ id, stages }) => {
  if (stages.length === 0) return null;
  const colWidth = `${100 / stages.length}%`;
  return (
    <div id={id} className="table-responsive" style={{ width: 0, minWidth: "100%" }}>
      <table
        className="table table-sm mb-0"
        style={{
          tableLayout: "fixed",
          width: "100%",
          minWidth: `${stages.length * 3}rem`,
          fontSize: "0.85rem",
        }}
      >
        <thead>
          <tr>
            {stages.map((s, i) => (
              <th
                key={`h-${i}`}
                title={s.name}
                className="fw-normal text-center text-body-secondary align-bottom"
                style={{
                  width: colWidth,
                  whiteSpace: "normal",
                  wordBreak: "normal",
                  // Uvnitř řádku DataTable by se zdědilo overflow-wrap: anywhere
                  // (ui.css) a názvy by se na mobilu dělily uprostřed slova;
                  // break-word nezmenšuje min-content – slova zůstanou celá
                  // a detail se při mnoha stanovištích posouvá do strany.
                  overflowWrap: "break-word",
                  fontSize: "0.75rem",
                  lineHeight: 1.15,
                  padding: "0.3rem 0.2rem",
                }}
              >
                {s.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {stages.map((s, i) => (
              <td
                key={`d-${i}`}
                className="text-center fw-semibold"
                style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
              >
                {fmtPoints(s.points)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  Blok jednoho závodu (akordeon)
// ─────────────────────────────────────────────────────────────

/** Rozbalování detailu stanovišť v tabulce závodu (sloupec se šipkou). */
interface StageToggle {
  /** Klíč (sequenceNumber) rozbaleného řádku; null = žádný. */
  openKey: string | null;
  onToggle: (row: RaceTableRow) => void;
  /** Id detailu stanovišť řádku (aria-controls tlačítka). */
  detailId: (row: RaceTableRow) => string;
}

/**
 * Sloupce tabulky závodu – kompaktní tabulka na všech šířkách (karty byly na
 * mobilu příliš vysoké): # a čísla na obsah, Jméno vyplní zbytek a smí se
 * zalomit, Kategorie od 576 px (pod tím drobně pod jménem), na konci šipka
 * u řádků se stanovišti (stageToggle = null → sloupec není).
 *
 * Šířka na mobilu: pevné sloupce (# s ikonou řazení, Body, %, šipka) a
 * nejužší Jméno (hlavička „Jméno" s ikonou řazení, minWidth) dají při
 * výchozím písmu ~250 px, takže se řádek vejde od 320 px displeje; při
 * systémovém písmu 130 % (obal WebView ho přebírá) ~300 px, tj. od 360 px.
 * Užší displej nebo větší písmo = tabulka se posune do strany ve vlastním
 * obalu, stránka ne.
 *
 * Body mají v celém závodě stejný počet desetinných míst (pointsDec
 * z pointsDecimals), procenta vždy jedno – čísla tak stojí zarovnaná pod
 * sebou a střelci je snadno porovnají. Body záměrně nejsou řaditelné:
 * ikona řazení stojí v DataTable za popiskem, takže by „Body" nestálo nad
 * čísly; řazení podle # dává totéž pořadí (výsledky jsou seřazené podle
 * bodů sestupně – sortRaceEntries).
 *
 * Od 768 px mají Body a „% z vítěze" stejnou šířku jako dřív (6 a 6,5 rem
 * včetně odsazení buňky) – drží ji min-width popisku v hlavičce; DataTable
 * šířku sloupce podle breakpointu neumí a pevná šířka by rozbila mobil.
 */
const buildRaceColumns = (
  topScore: number,
  pointsDec: number,
  showSequenceNumbers: boolean,
  stageToggle: StageToggle | null,
  /** Klíče záznamů, jejichž pořadí určil rozstřel (štítek R). */
  shootOff: ReadonlySet<string>
): DataTableColumn<RaceTableRow>[] => {
  const columns: DataTableColumn<RaceTableRow>[] = [
    {
      key: "rank",
      header: "#",
      headerTitle: "Pořadí",
      width: "fit",
      align: "center",
      numeric: true,
      sortable: true,
      sortValue: (r) => r.rank,
      render: (r) => <span className="text-body-secondary">{r.rank}</span>,
    },
    {
      key: "name",
      header: "Jméno",
      width: "flex",
      minWidth: "4.5rem",
      sortable: true,
      sortValue: (r) => resultEntryName(r.entry),
      render: (r) => {
        const category = displayEntryCategory(r.entry);
        const name = resultEntryName(r.entry);
        return (
          <>
            {name}
            {/* Bez jména je číslo už v náhradním jménu („#012") */}
            {showSequenceNumbers && name !== `#${r.entry.sequenceNumber}` && (
              <span className="text-body-secondary small"> #{r.entry.sequenceNumber}</span>
            )}
            {shootOff.has(r.entry.sequenceNumber) && <ShootOffBadge />}
            {/* Sloupec Kategorie je pod 576 px skrytý – kategorie drobně pod jménem */}
            {category && (
              <span className="d-block d-sm-none small text-body-secondary">{category}</span>
            )}
          </>
        );
      },
    },
    {
      key: "category",
      header: "Kategorie",
      width: CATEGORY_COL_WIDTH,
      align: "center",
      nowrap: true,
      hideBelow: "sm",
      sortable: true,
      sortType: "number",
      sortValue: (r) => categoryOrder(resolveEntryCategoryId(r.entry)),
      render: (r) => displayEntryCategory(r.entry),
    },
    {
      key: "points",
      // Popisek zarovnaný vpravo nad čísly; min-width platí od 768 px (viz výše)
      header: (
        <span className="d-md-inline-block text-end" style={{ minWidth: "5rem" }}>
          Body
        </span>
      ),
      headerTitle: "Nástřel",
      width: "fit",
      numeric: true,
      render: (r) => (
        <span className="fw-semibold">{fmtFixed(r.entry.matchPoints, pointsDec)}</span>
      ),
    },
    {
      key: "percent",
      // Na mobilu jen „%" (šetří šířku), od 576 px celý název, od 768 px
      // i původní šířka sloupce; vysvětlení v title
      header: (
        <>
          <span className="d-sm-none">%</span>
          <span
            className="d-none d-sm-inline d-md-inline-block text-end"
            style={{ minWidth: "5.5rem" }}
          >
            % z vítěze
          </span>
        </>
      ),
      headerTitle: "Procento z výsledku vítěze celého závodu",
      width: "fit",
      numeric: true,
      render: (r) => (
        <span className="text-body-secondary">
          {topScore > 0 ? fmtFixed(computeRacePercent(r.entry, { topScore }), 1) : "–"}
        </span>
      ),
    },
  ];
  if (stageToggle) {
    // Jen nápověda, že řádek jde rozbalit: klepnout jde na celý řádek
    // (onRowClick); tlačítko je cíl pro klávesnici a nese aria-expanded.
    // Velké jako ikona v písmu buňky (roste se systémovým písmem jako text,
    // btn-sm by navíc narostl na 44 px) a záporný okraj ubere polovinu
    // odsazení buňky (DataTable nemá odsazení per sloupec) – sloupec má tak
    // ~1,4 rem místo 2,5 rem a řádek se vejde na užší displej. Dotykový cíl
    // je celý řádek. Název nese jméno střelce, ať jdou tlačítka rozlišit.
    columns.push({
      key: "stages",
      header: <span className="visually-hidden">Stanoviště</span>,
      width: "fit",
      align: "center",
      render: (r) => {
        if (!hasStages(r)) return null;
        const isOpen = stageToggle.openKey === r.entry.sequenceNumber;
        return (
          <button
            type="button"
            className="btn btn-link border-0 p-0 lh-1 text-body-secondary"
            style={{ fontSize: "inherit", margin: "0 -0.25rem" }}
            aria-expanded={isOpen}
            aria-controls={isOpen ? stageToggle.detailId(r) : undefined}
            aria-label={`${isOpen ? "Skrýt" : "Zobrazit"} stanoviště – ${resultEntryName(r.entry)}`}
            onClick={() => stageToggle.onToggle(r)}
          >
            <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`} aria-hidden="true" />
          </button>
        );
      },
    });
  }
  return columns;
};

interface RaceBlockProps {
  race: Race;
  state: RaceResultsState | undefined;
  /** Počet střelců z agregačního dotazu (0 = neznámý) – jen pro odznak. */
  fallbackCount: number;
  open: boolean;
  onToggle: () => void;
  /** Nové načtení po chybě. */
  onRetry: () => void;
  category: ResultCategoryId | "all";
  onClearCategory: () => void;
  mySeq: ShooterId;
  /** Přihlášený střelec pro „Sdílet můj výsledek" (isMyEntry – i podle uid a jména). */
  me: Me;
  showSequenceNumbers: boolean;
  /** Registrace elementu bloku (odscrollování po požadavku stránky). */
  registerRef: (raceId: string, el: HTMLDivElement | null) => void;
}

const RaceBlock: React.FC<RaceBlockProps> = ({
  race,
  state,
  fallbackCount,
  open,
  onToggle,
  onRetry,
  category,
  onClearCategory,
  mySeq,
  me,
  showSequenceNumbers,
  registerRef,
}) => {
  const [openDetails, setOpenDetails] = useState<string | null>(null);
  const bodyId = `race-${race.id}-body`;
  // Během nového čtení (nová publikace) zůstávají vidět výsledky předchozí
  const results = resultsOf(state);
  const loading = state !== undefined && state.status === "loading";
  const failed = state !== undefined && state.status === "error";

  const setRef = useCallback(
    (el: HTMLDivElement | null) => registerRef(race.id, el),
    [registerRef, race.id]
  );

  // Odznak počtu: načtené výsledky, jinak metadata publikace, jinak agregační dotaz
  const count = results
    ? results.length
    : race.resultsPublishedCount || fallbackCount;

  // Pořadí = index ve vyfiltrovaném seznamu (starší závody jako dřív);
  // procenta k vítězi CELÉHO závodu, ne vyfiltrované kategorie (jedna
  // definice s ročním hodnocením). Filtr kategorie řadí znovu jen
  // v kategorii (rozstřel zadaný v kategorii platí v kategorii, fáze 4a C2);
  // bez rozstřelů s otiskem totéž co dřívější filtr seřazeného celku.
  // Fáze 4b: závod živého bodování ukončený od nasazení 4b řadí shodu podle
  // pravidel LKS (rozstřel o prvních X míst, počet zásahů, stejné místo –
  // sdílené místo = stejné číslo), jako tabule a Živé výsledky.
  const ranked = useMemo(() => {
    if (!results) return null;
    return category === "all"
      ? rankRaceEntries(results, race)
      : rankCategoryEntries(results, (r) => resolveEntryCategoryId(r.entry) === category, race);
  }, [results, category, race]);
  const rows = useMemo<RaceTableRow[]>(() => {
    if (!ranked) return [];
    return ranked.ordered.map((r) => ({ ...r, rank: ranked.places.get(r) as number }));
  }, [ranked]);
  const topScore = useMemo(() => raceTopScore(results), [results]);
  // Desetinná místa bodů podle CELÉHO závodu (ne filtru kategorie) – stejná
  // i ve sloupci závodu v ročním hodnocení
  const pointsDec = useMemo(
    () => pointsDecimals((results || []).map((r) => r.entry.matchPoints)),
    [results]
  );
  const anyStages = rows.some(hasStages);
  // Upozornění u filtru kategorie: publikace dřívější verzí kategorie úplně nezná
  const legacyLive = useMemo(
    () => !!results && results.some(isLegacyLiveResult),
    [results]
  );

  // Rozstřely – nad všemi výsledky závodu (shoda je o celkovém pořadí);
  // rozstřely s otiskem skupiny (fáze 4a, C2) nad zobrazeným seznamem: při
  // filtru kategorie skupiny kategorie, starší rozstřely dál z celku
  const shootOff = useMemo(() => {
    // Pravidla 4b: R u shody rozhodnuté rozstřelem (prvních X míst) v zobrazeném pořadí
    if (ranked && ranked.ties) {
      const keys = new Set<string>();
      ranked.ties.forEach((info, r) => {
        if (info.method === "shootOff") keys.add(r.entry.sequenceNumber);
      });
      return keys;
    }
    const keyOf = (r: RaceResult) => r.entry.sequenceNumber;
    const pointsOf = (r: RaceResult) => r.entry.matchPoints;
    const tbOf = (r: RaceResult) => tiebreakerOf(r.entry);
    const idOf = (r: RaceResult) => shootOffIdOf(r.entry);
    const all = results || [];
    if (category === "all") return shootOffKeys(all, keyOf, pointsOf, tbOf, idOf);
    const keys = shootOffKeys(all, keyOf, pointsOf, tbOf, idOf, "legacy");
    shootOffKeys(rows, keyOf, pointsOf, tbOf, idOf, "tagged").forEach((k) => keys.add(k));
    return keys;
  }, [results, rows, category, ranked]);

  // Jeden rozbalený řádek; klik/tap na řádek i tlačítko se šipkou
  const toggleDetails = useCallback((row: RaceTableRow) => {
    if (!hasStages(row)) return;
    const key = row.entry.sequenceNumber;
    setOpenDetails((prev) => (prev === key ? null : key));
  }, []);

  // Id detailu stanovišť (aria-controls); mezery by id rozdělily na víc
  // odkazů. String(): starší dokumenty můžou mít číslo místo textu.
  const detailIdOf = useCallback(
    (row: RaceTableRow) =>
      `race-${race.id}-stages-${String(row.entry.sequenceNumber).replace(/\s+/g, "_")}`,
    [race.id]
  );

  const columns = useMemo(
    () =>
      buildRaceColumns(
        topScore,
        pointsDec,
        showSequenceNumbers,
        anyStages
          ? { openKey: openDetails, onToggle: toggleDetails, detailId: detailIdOf }
          : null,
        shootOff
      ),
    [topScore, pointsDec, showSequenceNumbers, anyStages, openDetails, toggleDetails, detailIdOf, shootOff]
  );
  const anyShootOff = rows.some((r) => shootOff.has(r.entry.sequenceNumber));

  // Sdílení výsledku přihlášeného střelce (obrázek) – z nefiltrovaných výsledků
  const myShare = useMemo<ShareResultData | null>(() => {
    if (!results || me.sequenceNumber === null || me.sequenceNumber === undefined) return null;
    const index = results.findIndex((r) => isMyEntry(r.entry, me));
    if (index < 0) return null;
    const mine = results[index];
    const categoryId = resolveEntryCategoryId(mine.entry);
    // Místo celkem a v kategorii jako tabulka (4b: sdílené místo)
    const overall = rankRaceEntries(results, race);
    const inCategory = categoryId
      ? rankCategoryEntries(results, (r) => resolveEntryCategoryId(r.entry) === categoryId, race)
      : null;
    // Štítek R jako tabulka celku (4b: rozstřel o prvních X míst)
    const mineShootOff = overall.ties
      ? (overall.ties.get(mine) || { method: null }).method === "shootOff"
      : shootOff.has(mine.entry.sequenceNumber);
    // Uložené jméno, když je bezpečné; e-mail nebo uid ze starší publikace
    // nahradí jméno z vlastního profilu (výsledek je střelcův), jinak „St. č."
    const ownUid = me.uid || undefined;
    const shooterName =
      safeShooterName(
        mine.entry.rawName,
        typeof mine.entry.userId === "string" && mine.entry.userId ? mine.entry.userId : ownUid
      ) ||
      profilePublicName({ firstName: me.firstName, lastName: me.lastName }, ownUid) ||
      resultEntryName(mine.entry);
    return {
      raceName: race.name,
      date: race.date,
      disciplineLabel: disciplineOf(race).label,
      shooterName,
      rank: overall.places.get(mine) as number,
      of: results.length,
      categoryId,
      categoryRank: inCategory ? (inCategory.places.get(mine) ?? null) : null,
      categoryOf: inCategory ? inCategory.ordered.length : null,
      points: mine.entry.matchPoints,
      pointsDecimals: pointsDec,
      percent: computeRacePercent(mine.entry, { topScore }),
      shootOff: mineShootOff,
    };
  }, [results, me, race, pointsDec, topScore, shootOff]);
  // V obalu aplikace (WebView) stažení ani sdílení souboru nefunguje
  const [inApp] = useState(() => isNativeApp() || !canOpenExternalLinks());

  let body: React.ReactNode;
  if (state && state.status === "error") {
    body = <ErrorState error={state.error} onRetry={onRetry} />;
  } else if (!results) {
    body = <LoadingState text="Načítám výsledky…" />;
  } else if (results.length === 0) {
    body = (
      <EmptyState
        icon="bi-trophy"
        title="Zatím bez výsledků"
        text="Výsledky tohoto závodu ještě nebyly zveřejněny."
      />
    );
  } else if (rows.length === 0) {
    // Nováčky a nečleny zařazuje až publikace z live bodování od kroku 9 (pole
    // categoryId). Starší publikace pole nemá – říct to rovnou (po nové
    // publikaci zpráva zmizí).
    body = (
      <EmptyState
        icon="bi-funnel"
        title="Žádné výsledky pro vybranou kategorii"
        text={legacyLive ? LEGACY_LIVE_NOTE : undefined}
        action={{ label: "Zrušit filtr kategorie", onClick: onClearCategory }}
      />
    );
  } else {
    body = (
      <>
        {myShare && (
          <div className="d-flex justify-content-end mb-2">
            <ShareResultButton data={myShare} nativeApp={inApp} />
          </div>
        )}
        {category !== "all" && legacyLive ? (
          <p className="small text-body-secondary mb-2">
            <i className="bi bi-info-circle me-1" aria-hidden="true" />
            {LEGACY_LIVE_NOTE}
          </p>
        ) : null}
        <DataTable<RaceTableRow>
          columns={columns}
          rows={rows}
          rowKey={(r) => r.entry.sequenceNumber}
          onRowClick={anyStages ? toggleDetails : undefined}
          rowProps={(r) => ownRowProps(isSameShooter(mySeq, r.entry.sequenceNumber))}
          expanded={(r) =>
            openDetails === r.entry.sequenceNumber && hasStages(r) ? (
              <StageDetail id={detailIdOf(r)} stages={r.stages} />
            ) : null
          }
          mobile="scroll"
          dense
        />
        {anyShootOff && (
          <p className="small text-body-secondary mt-2 mb-0">
            <span className="badge text-bg-warning me-1" aria-hidden="true">
              R
            </span>
            = pořadí při shodě bodů určil rozstřel.
          </p>
        )}
      </>
    );
  }

  return (
    <div className="card mb-2" ref={setRef}>
      <h3 className="m-0">
        {/* Hlavička akordeonu (ui.css .lks-accordion__toggle: hover, fokus,
            zvýraznění otevřeného stavu; odstupy dětí řeší sourozenecký margin,
            proto bez ms-2). Otevřený blok má spodní rohy rovné kvůli border-top těla. */}
        <button
          type="button"
          className={open ? "lks-accordion__toggle rounded-bottom-0" : "lks-accordion__toggle"}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <DisciplineBadge race={race} />
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <span className="d-block" style={{ overflowWrap: "anywhere" }}>
              {race.name}
            </span>
            <span className="d-block small fw-normal text-body-secondary">
              {fmtRaceDate(race.date)}
            </span>
          </span>
          {loading && (
            <span
              className="spinner-border spinner-border-sm text-body-secondary"
              role="status"
              aria-label="Načítám výsledky"
            />
          )}
          {failed && (
            <i
              className="bi bi-exclamation-triangle-fill text-warning"
              role="img"
              title="Výsledky se nepodařilo načíst"
              aria-label="Výsledky se nepodařilo načíst"
            />
          )}
          {!loading && count > 0 && (
            <span className="badge text-bg-secondary">{shooterCountLabel(count)}</span>
          )}
          {!loading && results !== null && results.length === 0 && (
            <span className="badge text-bg-light">bez výsledků</span>
          )}
          <i
            className={`bi bi-chevron-${open ? "up" : "down"} text-body-secondary`}
            aria-hidden="true"
          />
        </button>
      </h3>
      {open && (
        <div id={bodyId} className="card-body border-top p-2 p-md-3">
          {body}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  Roční hodnocení (per rok a disciplína)
// ─────────────────────────────────────────────────────────────

/**
 * Desetinná místa bodů v ročním hodnocení (režim „Body"): každý sloupec
 * závodu a sloupec průměru má jeden pevný počet (pointsDecimals), aby čísla
 * stála zarovnaná pod sebou. Procenta mají vždy jedno místo.
 */
interface SeasonPointsFormat {
  /** Podle všech výsledků závodu – stejně jako v tabulce závodu. */
  byRace: { [raceId: string]: number };
  /** Podle zobrazených průměrů (nejvýš 1 místo jako výpočet průměru). */
  average: number;
}

/** Sloupce: Jméno (s pořadím) pružné a přilepené vlevo, Kategorie pevná, závody a průměr na obsah. */
const buildSeasonColumns = (
  races: SeasonRace[],
  mode: SeasonValueMode,
  bestN: BestRacesCount,
  showSequenceNumbers: boolean,
  pointsFormat: SeasonPointsFormat
): DataTableColumn<SeasonTableRow>[] => [
  {
    key: "name",
    header: "Jméno",
    headerTitle: "Pořadí a jméno střelce",
    width: "flex",
    minWidth: "10rem",
    sortable: true,
    sortValue: (row) => row.name,
    render: (row) => (
      <>
        <span className="text-body-secondary">{row.rank}.</span> {row.name}
        {showSequenceNumbers && row.name !== `#${row.sequenceNumber}` && (
          <span className="text-body-secondary small"> #{row.sequenceNumber}</span>
        )}
      </>
    ),
  },
  {
    key: "category",
    header: "Kategorie",
    width: CATEGORY_COL_WIDTH,
    align: "center",
    nowrap: true,
    sortable: true,
    sortType: "number",
    sortValue: (row) => categoryOrder(row.categoryId),
    // Nezařazené hodnoty (nečlen, cizí text importu) se ukážou, jak jsou uložené
    render: (row) => (row.categoryId ? categoryLabel(row.categoryId) : row.category),
  },
  ...races.map(
    (race): DataTableColumn<SeasonTableRow> => ({
      key: `race-${race.id}`,
      header: (
        <span className="d-inline-block small lh-sm">
          {shortRaceName(race.name)}
          <br />
          <span className="text-body-secondary fw-normal">{fmtShortDate(race.date)}</span>
        </span>
      ),
      headerTitle: `${race.name} (${fmtRaceDate(race.date)})`,
      width: "fit",
      numeric: true,
      render: (row) => {
        const value = row.raceValues[race.id];
        if (!value) return "";
        const text =
          mode === "percent"
            ? fmtFixed(value, 1)
            : fmtFixed(value, pointsFormat.byRace[race.id] || 0);
        return row.ignoredRaceIds.has(race.id) ? (
          <span
            className="text-decoration-line-through opacity-50"
            title="Nepočítá se do průměru"
          >
            {text}
          </span>
        ) : (
          text
        );
      },
    })
  ),
  {
    key: "average",
    header: mode === "percent" ? "Prům. %" : "Prům. body",
    headerTitle:
      bestN === "all"
        ? "Průměr ze všech závodů roku"
        : `Průměr z nejlepších ${bestN} závodů`,
    width: "fit",
    numeric: true,
    sortable: true,
    sortValue: (row) => row.averageValue,
    render: (row) => (
      <span className="fw-bold">
        {mode === "percent"
          ? fmtFixed(row.averageValue, 1)
          : fmtFixed(row.averageValue, pointsFormat.average)}
      </span>
    ),
  },
];

interface SeasonBodyProps {
  year: number;
  discipline: Discipline;
  allRaces: Race[];
  entriesByRace: EntriesByRace;
  resultsByRace: ResultsByRace;
  category: ResultCategoryId | "all";
  settings: SeasonSettings;
  /** N už oříznuté na počet hodnocených závodů (viz SeasonBlock). */
  effectiveN: BestRacesCount;
  raceCount: number;
  onSettingsChange: (settings: SeasonSettings) => void;
  onRetry: () => void;
  mySeq: ShooterId;
  showSequenceNumbers: boolean;
}

/** Obsah otevřeného bloku: ovládání N a režimu + tabulka (mountuje se až po otevření). */
const SeasonBody: React.FC<SeasonBodyProps> = ({
  year,
  discipline,
  allRaces,
  entriesByRace,
  resultsByRace,
  category,
  settings,
  effectiveN,
  raceCount,
  onSettingsChange,
  onRetry,
  mySeq,
  showSequenceNumbers,
}) => {
  const idBase = `season-${year}-${discipline.id}`;

  const ranking = useMemo(
    () =>
      computeSeasonRanking({
        races: allRaces,
        entriesByRace,
        year,
        discipline: discipline.id,
        mode: settings.mode,
        bestN: effectiveN,
        category,
      }),
    [allRaces, entriesByRace, year, discipline.id, settings.mode, effectiveN, category]
  );

  const rows = useMemo<SeasonTableRow[]>(
    () => ranking.rows.map((row, index) => ({ ...row, rank: index + 1 })),
    [ranking]
  );

  const pointsFormat = useMemo<SeasonPointsFormat>(() => {
    const byRace: { [raceId: string]: number } = {};
    ranking.races.forEach((race) => {
      byRace[race.id] = pointsDecimals(
        (entriesByRace[race.id] || []).map((r) => r.entry.matchPoints)
      );
    });
    return { byRace, average: pointsDecimals(rows.map((row) => row.averageValue), 1) };
  }, [ranking.races, entriesByRace, rows]);

  const columns = useMemo(
    () =>
      buildSeasonColumns(
        ranking.races,
        settings.mode,
        effectiveN,
        showSequenceNumbers,
        pointsFormat
      ),
    [ranking.races, settings.mode, effectiveN, showSequenceNumbers, pointsFormat]
  );

  // Nenačtené závody: ještě se načítají, nebo jejich čtení selhalo (chybné se
  // do výpočtu nepředávají, jinak by se počítaly jako „bez výsledků")
  const { pending, failed } = useMemo(() => {
    const pendingIds: string[] = [];
    const failedIds: string[] = [];
    ranking.missingRaceIds.forEach((id) => {
      const s = resultsByRace[id];
      if (s && s.status === "error") failedIds.push(id);
      else pendingIds.push(id);
    });
    return { pending: pendingIds, failed: failedIds };
  }, [ranking.missingRaceIds, resultsByRace]);

  // Filtr kategorie nad závody zveřejněnými dřívější verzí (live bez categoryId):
  // nováčci a nečlenové v nich nejsou zařazení a kategorie střelce se bere
  // z prvního závodu sezóny – hodnocení kategorie tak nemusí být úplné.
  const legacyRaceCount = useMemo(
    () =>
      category === "all"
        ? 0
        : ranking.races.filter((race) =>
            (entriesByRace[race.id] || []).some(isLegacyLiveResult)
          ).length,
    [category, ranking.races, entriesByRace]
  );

  const firstFailure = failed.length > 0 ? resultsByRace[failed[0]] : undefined;
  const failureMessage =
    firstFailure && firstFailure.status === "error"
      ? firebaseErrorMessage(firstFailure.error)
      : "";

  let status: "idle" | "loading" | "empty" = "idle";
  if (rows.length === 0) status = pending.length > 0 ? "loading" : "empty";

  return (
    <>
      <div className="row g-2">
        <div className="col-sm-6">
          <Field id={`${idBase}-bestn`} label="Započítané závody">
            <select
              className="form-select"
              value={String(effectiveN)}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  bestN: e.target.value === "all" ? "all" : Number(e.target.value),
                })
              }
            >
              {bestRacesOptions(raceCount).map((n) => (
                <option key={String(n)} value={String(n)}>
                  {bestRacesLabel(n)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="col-sm-6">
          <ChoiceGroup
            id={`${idBase}-mode`}
            label="Zobrazit"
            options={MODE_OPTIONS}
            value={settings.mode}
            onChange={(mode) => onSettingsChange({ ...settings, mode })}
            columns={2}
          />
        </div>
      </div>
      <p className="small text-body-secondary">
        Procenta jsou podíl na výsledku vítěze závodu. Průměr se počítá z vybraného
        počtu nejlepších závodů (neúčast = 0); přeškrtnuté hodnoty se do něj nepočítají.
      </p>

      {failed.length > 0 && (
        <div className="alert alert-warning d-flex align-items-center py-2 small" role="alert">
          <i className="bi bi-exclamation-triangle me-2" aria-hidden="true" />
          <span className="flex-grow-1">
            Výsledky {failed.length} {failed.length === 1 ? "závodu" : "závodů"} se
            nepodařilo načíst – tabulka je neúplná. {failureMessage}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-2"
            onClick={onRetry}
          >
            Zkusit znovu
          </button>
        </div>
      )}

      {pending.length > 0 && rows.length > 0 && (
        <div className="mb-2">
          <LoadingState inline text="Načítám výsledky dalších závodů…" />
        </div>
      )}

      {legacyRaceCount > 0 && (
        <p className="small text-body-secondary mb-2">
          <i className="bi bi-info-circle me-1" aria-hidden="true" />
          {legacyRaceCount === 1
            ? "Jeden závod byl zveřejněn"
            : legacyRaceCount < 5
            ? `${legacyRaceCount} závody byly zveřejněny`
            : `${legacyRaceCount} závodů bylo zveřejněno`}{" "}
          dřívější verzí aplikace: nováčci v {legacyRaceCount === 1 ? "něm" : "nich"} nejsou
          označení a nečlenové nejsou zařazení do kategorií. Hodnocení kategorie proto
          nemusí být úplné.
        </p>
      )}

      <DataTable<SeasonTableRow>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.sequenceNumber}
        rowProps={(row) => ownRowProps(isSameShooter(mySeq, row.sequenceNumber))}
        mobile="scroll"
        stickyFirst
        dense
        status={status}
        loadingText="Načítám výsledky závodů…"
        emptyText="Žádné výsledky pro vybraný filtr."
      />
    </>
  );
};

interface SeasonBlockProps {
  year: number;
  discipline: Discipline;
  /** Hodnocené závody roku a disciplíny (seasonRaces). */
  races: Race[];
  allRaces: Race[];
  entriesByRace: EntriesByRace;
  resultsByRace: ResultsByRace;
  category: ResultCategoryId | "all";
  settings: SeasonSettings;
  onSettingsChange: (settings: SeasonSettings) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRetry: () => void;
  mySeq: ShooterId;
  showSequenceNumbers: boolean;
}

const SeasonBlock: React.FC<SeasonBlockProps> = ({
  year,
  discipline,
  races,
  allRaces,
  entriesByRace,
  resultsByRace,
  category,
  settings,
  onSettingsChange,
  open,
  onOpenChange,
  onRetry,
  mySeq,
  showSequenceNumbers,
}) => {
  const raceCount = races.length;
  // N větší než počet závodů roku = všechny závody (min(N, počet) v algoritmu
  // dává totéž) – select tak vždy ukazuje platnou volbu
  const effectiveN: BestRacesCount =
    typeof settings.bestN === "number" && settings.bestN > raceCount
      ? "all"
      : settings.bestN;
  const summary = `${bestRacesLabel(effectiveN)} · ${
    settings.mode === "percent" ? "procenta" : "body"
  }`;

  return (
    <Section
      title={`Celkové výsledky za rok ${year} – ${discipline.label}`}
      icon="bi-bar-chart"
      description={summary}
      collapsible
      open={open}
      onOpenChange={onOpenChange}
    >
      <SeasonBody
        year={year}
        discipline={discipline}
        allRaces={allRaces}
        entriesByRace={entriesByRace}
        resultsByRace={resultsByRace}
        category={category}
        settings={settings}
        effectiveN={effectiveN}
        raceCount={raceCount}
        onSettingsChange={onSettingsChange}
        onRetry={onRetry}
        mySeq={mySeq}
        showSequenceNumbers={showSequenceNumbers}
      />
    </Section>
  );
};

// ─────────────────────────────────────────────────────────────
//  Obrazovka
// ─────────────────────────────────────────────────────────────

const UserResults: React.FC<UserResultsProps> = ({ openRequest = null }) => {
  const isAdmin = useIsAdmin();
  const { user, userDoc } = useCurrentUser();
  const mySeq: ShooterId = userDoc ? userDoc.sequenceNumber : null;
  const me = useMemo<Me>(
    () => ({
      uid: user ? user.uid : null,
      sequenceNumber: mySeq,
      firstName: userDoc ? userDoc.firstName : null,
      lastName: userDoc ? userDoc.lastName : null,
    }),
    [user, userDoc, mySeq]
  );

  // ── Filtry ──────────────────────────────────────────────────
  const [filterYear, setFilterYear] = useState<number | "all">(initialFilterYear);
  const [filterDiscipline, setFilterDiscipline] = useState<DisciplineId | "all">(
    readStoredDiscipline
  );
  const [filterCategory, setFilterCategory] = useState<ResultCategoryId | "all">("all");
  const [showSequenceNumbers, setShowSequenceNumbers] = useState(false);

  // ── Otevřené bloky a nastavení ročního hodnocení ─────────────
  const [openRaceId, setOpenRaceId] = useState<string | null>(null);
  const [openSeasonKey, setOpenSeasonKey] = useState<string | null>(null);
  const [seasonSettings, setSeasonSettings] = useState<SeasonSettingsByDiscipline>(
    initialSeasonSettings
  );

  const { races, allRaces, status, error, reload } = useRaces({
    year: filterYear === "all" ? undefined : filterYear,
    discipline: filterDiscipline,
  });

  // Roky pro filtr (0 = nečitelné datum se vynechá), nejnovější první
  const years = useMemo(
    () =>
      Array.from(new Set(allRaces.map((r) => r.year)))
        .filter(Boolean)
        .sort((a, b) => b - a),
    [allRaces]
  );

  // Vybraný rok bez závodů (typicky začátek sezóny) → „Všechny". Layout
  // efekt: přepne ještě před vykreslením, takže po prvním načtení seznamu
  // neblikne prázdný stav (po návratu na stránku to řeší už initialFilterYear).
  useLayoutEffect(() => {
    if (allRaces.length === 0) return;
    setFilterYear((prev) =>
      prev === "all" ||
      allRaces.some((r) => r.year === prev && r.enableResults)
        ? prev
        : defaultFilterYear(allRaces)
    );
  }, [allRaces]);

  // ── Výsledky závodů ─────────────────────────────────────────
  // Stav je v useState kvůli renderu a zároveň v ref kvůli guardu proti
  // dvojímu načtení (closure v useCallback by viděla zastaralý stav – #28).
  const [resultsByRace, setResultsByRace] = useState<ResultsByRace>({});
  const resultsRef = useRef<ResultsByRace>({});
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const setRaceState = useCallback((raceId: string, state: RaceResultsState) => {
    resultsRef.current = { ...resultsRef.current, [raceId]: state };
    setResultsByRace(resultsRef.current);
  }, []);

  /**
   * Načte výsledky závodu, pokud pro AKTUÁLNÍ publikaci nejsou načtené ani se
   * nenačítají (po chybě znovu). Jiné race.resultsPublishedAt než u dosavadního
   * stavu (porovnání millis, ne objektů Timestamp) = admin výsledky
   * (pře)publikoval → nové čtení; dosavadní výsledky zůstávají zobrazené
   * jako `stale`, než dorazí nové. Výsledek čtení, které mezitím překonala
   * ještě novější publikace, se zahodí.
   */
  const loadResults = useCallback(
    (race: Race) => {
      const publishedAt = race.resultsPublishedAt;
      const current = resultsRef.current[race.id];
      if (current && current.status !== "error" && current.publishedAt === publishedAt) {
        return;
      }
      setRaceState(race.id, { status: "loading", publishedAt, stale: resultsOf(current) });
      const outdated = () => {
        const latest = resultsRef.current[race.id];
        return latest !== undefined && latest.publishedAt !== publishedAt;
      };
      loadRaceResults(race.id, publishedAt).then(
        (bundle) => {
          if (aliveRef.current && !outdated()) {
            setRaceState(race.id, { status: "ready", publishedAt, results: bundle.results });
          }
        },
        (err: unknown) => {
          reportError("UserResults.loadResults", err, { raceId: race.id });
          if (aliveRef.current && !outdated()) {
            setRaceState(race.id, { status: "error", publishedAt, error: err });
          }
        }
      );
    },
    [setRaceState]
  );

  /** Hodnocené závody roku (a disciplíny) jako Race – seasonRaces vrací tytéž objekty, jen filtruje a řadí. */
  const seasonRacesOf = useCallback(
    (year: number, discipline: DisciplineId | "all" = "all"): Race[] =>
      seasonRaces(allRaces, year, discipline) as Race[],
    [allRaces]
  );

  const visibleYears = useMemo(
    () => (filterYear === "all" ? years : [filterYear]),
    [filterYear, years]
  );

  // Automatické načtení výsledků: hodnocené závody zobrazených roků a
  // otevřený závod – jen dosud nezkoušené, nebo načtené pro STARŠÍ publikaci
  // (obnovený seznam přinesl jiné resultsPublishedAt); chybné znovu načte
  // tlačítko v bloku, po nové publikaci se zkusí samy
  useEffect(() => {
    const wanted: Race[] = [];
    visibleYears.forEach((year) => {
      seasonRacesOf(year).forEach((race) => wanted.push(race));
    });
    const openRace = openRaceId ? allRaces.find((r) => r.id === openRaceId) : undefined;
    if (openRace) wanted.push(openRace);
    wanted.forEach((race) => {
      const current = resultsRef.current[race.id];
      if (!current || current.publishedAt !== race.resultsPublishedAt) loadResults(race);
    });
  }, [visibleYears, seasonRacesOf, allRaces, openRaceId, loadResults]);

  /** Nové načtení závodů roku a disciplíny, které selhaly (nebo se ještě nezkoušely). */
  const retrySeason = useCallback(
    (year: number, discipline: DisciplineId) => {
      seasonRacesOf(year, discipline).forEach((race) => {
        const s = resultsRef.current[race.id];
        if (!s || s.status === "error") loadResults(race);
      });
    },
    [seasonRacesOf, loadResults]
  );

  // Závody v seznamu: po filtru roku/disciplíny, bez akcí bez výsledků
  const visibleRaces = useMemo(() => races.filter((r) => r.enableResults), [races]);

  // Záložní počet střelců pro závody bez resultsPublishedCount (starší
  // importy), jejichž výsledky se nenačítají – agregační dotaz vrátí jen číslo
  const [countByRace, setCountByRace] = useState<{ [raceId: string]: number }>({});
  const countRequested = useRef<Set<string>>(new Set());
  useEffect(() => {
    visibleRaces.forEach((race) => {
      if (race.resultsPublishedCount > 0 || countRequested.current.has(race.id)) return;
      if (resultsRef.current[race.id]) return;
      countRequested.current.add(race.id);
      countRaceResults(race.id).then(
        (n) => {
          if (aliveRef.current && n > 0) {
            setCountByRace((prev) => ({ ...prev, [race.id]: n }));
          }
        },
        (err: unknown) => {
          reportWarning("UserResults.countRaceResults", err, { raceId: race.id });
        }
      );
    });
  }, [visibleRaces]);

  // Výsledky pro roční hodnocení: závody ve stavu ready a během nového čtení
  // po publikaci výsledky předchozí (tabulka neposkočí); první načtení a
  // chybné zůstávají „nenačtené" → missingRaceIds
  const entriesByRace = useMemo<EntriesByRace>(() => {
    const map: EntriesByRace = {};
    Object.keys(resultsByRace).forEach((id) => {
      const results = resultsOf(resultsByRace[id]);
      if (results) map[id] = results;
    });
    return map;
  }, [resultsByRace]);

  // ── Otevření závodu z karty ukončeného závodu (UserResultsPage) ──
  const handledRequestRef = useRef<number | null>(null);
  const pendingScrollRef = useRef<string | null>(null);
  const blockRefs = useRef<{ [raceId: string]: HTMLDivElement | null }>({});
  const registerBlockRef = useCallback((raceId: string, el: HTMLDivElement | null) => {
    blockRefs.current[raceId] = el;
  }, []);

  useEffect(() => {
    if (!openRequest || handledRequestRef.current === openRequest.token) return;
    const race = allRaces.find((r) => r.id === openRequest.raceId);
    if (!race) return; // závody ještě nejsou načtené – zkusí se po načtení
    handledRequestRef.current = openRequest.token;
    // Filtry upravit jen tak, aby byl závod vidět
    setFilterYear((prev) => (prev === "all" || prev === race.year ? prev : race.year));
    setFilterDiscipline((prev) =>
      prev === "all" || prev === race.discipline ? prev : race.discipline
    );
    setOpenRaceId(race.id);
    pendingScrollRef.current = race.id;
    loadResults(race);
  }, [openRequest, allRaces, loadResults]);

  // Po vykreslení otevřeného bloku k němu odscrollovat (bez deps: čeká, až je v DOM)
  useEffect(() => {
    const id = pendingScrollRef.current;
    if (!id || openRaceId !== id) return;
    const el = blockRefs.current[id];
    if (!el) return;
    pendingScrollRef.current = null;
    scrollIntoMain(el);
  });

  // ── Moje sezóna: osobní přehled pro vybraný rok (ne „Všechny roky") ──
  const mySeasons = useMemo<MySeason[]>(() => {
    if (filterYear === "all" || mySeq === null || mySeq === undefined) return [];
    const seasons: MySeason[] = [];
    DISCIPLINE_LIST.forEach((discipline) => {
      if (filterDiscipline !== "all" && filterDiscipline !== discipline.id) return;
      const settings = seasonSettings[discipline.id];
      const season = computeMySeason({
        races: allRaces,
        entriesByRace,
        year: filterYear,
        discipline: discipline.id,
        me,
        mode: settings.mode,
        bestN: settings.bestN,
      });
      if (season) seasons.push(season);
    });
    return seasons;
  }, [filterYear, filterDiscipline, mySeq, me, allRaces, entriesByRace, seasonSettings]);

  // Stav načítání odjetých hodnocených závodů disciplín z filtru (i té, kde
  // zatím žádný můj výsledek není – mohl být právě v závodě, který selhal):
  // chybné se nenačtou samy – přehled je neúplný, nabídne se „Zkusit znovu"
  const mySeasonDisciplines = useMemo(
    () =>
      DISCIPLINE_LIST.filter((d) => filterDiscipline === "all" || filterDiscipline === d.id).map(
        (d) => d.id
      ),
    [filterDiscipline]
  );
  const mySeasonStatus = useMemo(() => {
    let pending = 0;
    let failed = 0;
    if (filterYear !== "all" && mySeq !== null && mySeq !== undefined) {
      mySeasonDisciplines.forEach((discipline) => {
        heldSeasonRaces(allRaces, filterYear, discipline, entriesByRace).forEach((race) => {
          const s = resultsByRace[race.id];
          if (s && s.status === "error") failed += 1;
          else if (!s || s.status === "loading") pending += 1;
        });
      });
    }
    return { pending, failed };
  }, [filterYear, mySeq, mySeasonDisciplines, allRaces, entriesByRace, resultsByRace]);
  const retryMySeason = useCallback(() => {
    if (filterYear === "all") return;
    mySeasonDisciplines.forEach((discipline) => retrySeason(filterYear, discipline));
  }, [filterYear, mySeasonDisciplines, retrySeason]);

  /** Klepnutí na závod v „Moje sezóna": otevřít jeho blok a odscrollovat k němu. */
  const openRaceFromSeason = useCallback(
    (raceId: string) => {
      const race = allRaces.find((r) => r.id === raceId);
      if (!race) return;
      if (openRaceId === race.id) {
        // Už otevřený blok: stejný stav by nic nepřekreslil – odscrollovat hned
        const el = blockRefs.current[race.id];
        if (el) scrollIntoMain(el);
        return;
      }
      setOpenRaceId(race.id);
      pendingScrollRef.current = race.id;
      loadResults(race);
    },
    [allRaces, loadResults, openRaceId]
  );

  // ── Akce ────────────────────────────────────────────────────
  const toggleRace = (race: Race) => {
    const willOpen = openRaceId !== race.id;
    // Ruční otevření/zavření ruší čekající odscrollování (Moje sezóna)
    pendingScrollRef.current = null;
    setOpenRaceId(willOpen ? race.id : null);
    if (willOpen) loadResults(race);
  };

  /**
   * Zrušení filtrů: disciplína a kategorie na „Všechny", rok na nejnovější,
   * který má závod s výsledky – „Všechny roky" by spustilo načtení výsledků
   * všech let. Uložená disciplína se nemění (není to volba v selectu).
   */
  const resetFilters = () => {
    setFilterYear(defaultFilterYear(allRaces));
    setFilterDiscipline("all");
    setFilterCategory("all");
  };

  const clearCategory = () => setFilterCategory("all");

  // Bloky ročního hodnocení: pro každý zobrazený rok a disciplínu s hodnocenými
  // závody (nikdy přes obě disciplíny dohromady – každá má vlastní hodnocení)
  const seasonBlocks = useMemo(() => {
    const blocks: Array<{ key: string; year: number; discipline: Discipline; races: Race[] }> = [];
    visibleYears.forEach((year) => {
      DISCIPLINE_LIST.forEach((discipline) => {
        if (filterDiscipline !== "all" && filterDiscipline !== discipline.id) return;
        const yearRaces = seasonRacesOf(year, discipline.id);
        if (yearRaces.length > 0) {
          blocks.push({ key: `${year}-${discipline.id}`, year, discipline, races: yearRaces });
        }
      });
    });
    return blocks;
  }, [visibleYears, filterDiscipline, seasonRacesOf]);

  // Roky v selectu: dokud se seznam načítá, aspoň vybraný rok (ať select nemá prázdnou hodnotu)
  const yearOptions = years.length > 0 || filterYear === "all" ? years : [filterYear];

  // ── Obsah pod filtrem ───────────────────────────────────────
  let content: React.ReactNode;
  if (status === "loading") {
    content = <LoadingState text="Načítám výsledky…" />;
  } else if (status === "error") {
    content = <ErrorState error={error} onRetry={reload} />;
  } else if (allRaces.length === 0) {
    content = (
      <EmptyState
        icon="bi-trophy"
        title="Zatím žádné výsledky"
        text="Jakmile klub zveřejní výsledky závodu, objeví se tady."
      />
    );
  } else if (visibleRaces.length === 0 && seasonBlocks.length === 0) {
    content = (
      <EmptyState
        icon="bi-funnel"
        title="Žádné výsledky pro zvolené filtry"
        text="Zkuste jiný rok nebo disciplínu."
        action={{ label: "Zrušit filtry", onClick: resetFilters }}
      />
    );
  } else {
    content = (
      <>
        {filterYear !== "all" && (
          <MySeasonSection
            year={filterYear}
            seasons={mySeasons}
            pending={mySeasonStatus.pending}
            failed={mySeasonStatus.failed}
            onRetry={retryMySeason}
            onOpenRace={openRaceFromSeason}
          />
        )}
        <SectionHeader
          title="Závody"
          icon="bi-calendar"
          headingLevel={2}
          className="mb-2"
          actions={
            visibleRaces.length > 0 ? (
              <span className="badge text-bg-secondary">{visibleRaces.length}</span>
            ) : undefined
          }
        />
        {visibleRaces.map((race) => (
          <RaceBlock
            key={race.id}
            race={race}
            state={resultsByRace[race.id]}
            fallbackCount={countByRace[race.id] || 0}
            open={openRaceId === race.id}
            onToggle={() => toggleRace(race)}
            onRetry={() => loadResults(race)}
            category={filterCategory}
            onClearCategory={clearCategory}
            mySeq={mySeq}
            me={me}
            showSequenceNumbers={showSequenceNumbers}
            registerRef={registerBlockRef}
          />
        ))}

        {seasonBlocks.length > 0 && (
          <div className="mt-4">
            {seasonBlocks.map((block) => (
              <SeasonBlock
                key={block.key}
                year={block.year}
                discipline={block.discipline}
                races={block.races}
                allRaces={allRaces}
                entriesByRace={entriesByRace}
                resultsByRace={resultsByRace}
                category={filterCategory}
                settings={seasonSettings[block.discipline.id]}
                onSettingsChange={(settings) =>
                  setSeasonSettings((prev) => ({ ...prev, [block.discipline.id]: settings }))
                }
                open={openSeasonKey === block.key}
                onOpenChange={(open) => setOpenSeasonKey(open ? block.key : null)}
                onRetry={() => retrySeason(block.year, block.discipline.id)}
                mySeq={mySeq}
                showSequenceNumbers={showSequenceNumbers}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Section title="Filtr" icon="bi-funnel">
        <div className="row g-2">
          <div className="col-6 col-md-4">
            <Field id="resultsYear" label="Rok" className="mb-0">
              <select
                className="form-select"
                value={filterYear === "all" ? "all" : String(filterYear)}
                onChange={(e) =>
                  setFilterYear(e.target.value === "all" ? "all" : Number(e.target.value))
                }
              >
                {/* Krátké „Všechny": v col-6 na 360px se delší text ořezával; kontext dává label */}
                <option value="all">Všechny</option>
                {yearOptions.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-6 col-md-4">
            <Field id="resultsDiscipline" label="Disciplína" className="mb-0">
              <select
                className="form-select"
                value={filterDiscipline}
                onChange={(e) => {
                  // Jen tady se volba ukládá jako výchozí pro příště –
                  // programové přepnutí (otevření závodu z karty) ji nepřepíše
                  const value =
                    e.target.value === "all" ? "all" : toDisciplineId(e.target.value);
                  setFilterDiscipline(value);
                  storeDiscipline(value);
                }}
              >
                <option value="all">Všechny</option>
                {DISCIPLINE_LIST.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-12 col-md-4">
            <Field
              id="resultsCategory"
              label="Kategorie"
              className="mb-0"
              help={
                filterCategory !== "all"
                  ? "Pořadí je v rámci kategorie, % je podíl na výsledku vítěze celého závodu."
                  : undefined
              }
            >
              <select
                className="form-select"
                value={filterCategory}
                onChange={(e) =>
                  setFilterCategory(
                    isResultCategoryId(e.target.value) ? e.target.value : "all"
                  )
                }
              >
                <option value="all">Všechny kategorie</option>
                {RESULT_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>
        {isAdmin && (
          <Toggle
            id="resultsShowSequenceNumbers"
            label="Zobrazit členská čísla"
            description="Členské číslo vedle jména; u střelců bez členského čísla startovní číslo (vidí jen správci)."
            checked={showSequenceNumbers}
            onChange={setShowSequenceNumbers}
            className="mt-3 mb-0"
          />
        )}
      </Section>

      {content}
    </>
  );
};

export default UserResults;
